import { useGame } from "./useGame.ts";

export interface ShootoutDisplay {
  homeShots: (boolean | null)[];
  guestShots: (boolean | null)[];
}

/**
 * Hook that returns shot arrays for display.
 * Each array contains boolean | null where:
 * - true = successful shot
 * - false = missed shot
 * - null = not yet shot
 */
export const useShootoutDisplay = (gameId?: string): ShootoutDisplay | null => {
  const game = useGame(gameId);

  if (!game.isSuccess) {
    return null;
  }

  const homeTeamId = game.data.actualTeams.homeTeamId;
  const guestTeamId = game.data.actualTeams.guestTeamId;
  const shots = game.data.shootOutShots;
  const minBalls = game.data.shootOutShotsNumber;
  const winnerDetermined = game.data.shootOutWinnerDetermined;

  // Build shot arrays directly from flat shots list
  const homeShots: (boolean | null)[] = [];
  const guestShots: (boolean | null)[] = [];

  for (const shot of shots) {
    if (shot.teamId === homeTeamId) {
      homeShots.push(shot.success);
    } else if (shot.teamId === guestTeamId) {
      guestShots.push(shot.success);
    }
  }

  if (winnerDetermined) {
    // When winner is determined: show actual shots, padded to minimum
    // Both teams show the same length for visual consistency
    const targetLength = Math.max(
      minBalls,
      homeShots.length,
      guestShots.length,
    );
    while (homeShots.length < targetLength) homeShots.push(null);
    while (guestShots.length < targetLength) guestShots.push(null);
  } else {
    // Determine which team shoots next based on last shot
    const lastShot = shots.at(-1);
    const homeShootsNext = !lastShot || lastShot.teamId === guestTeamId;

    // Team shooting next gets +1 for pending ball
    // Both teams show the same length (max of both targets)
    const homeTarget = homeShots.length + (homeShootsNext ? 1 : 0);
    const guestTarget = guestShots.length + (homeShootsNext ? 0 : 1);
    const displayLength = Math.max(minBalls, homeTarget, guestTarget);

    while (homeShots.length < displayLength) homeShots.push(null);
    while (guestShots.length < displayLength) guestShots.push(null);
  }

  return { homeShots, guestShots };
};
